import gql from 'graphql-tag';
import { PageInfoFragment } from '@app/pages/cmms/graphql/cmms-fragments';

export const AssetFragment = gql`
	fragment AssetFragment on AssetObjectType {
		created
		pk
		id
		ifcId {
			expressId
			modelId
			globalId
		}
		assetAttachedFiles {
			pk
			file {
				contentType
				name
				url
			}
			created
			createdBy {
				name
				lastName
				smallAvatarUrl
				firstName
			}
			classificationsV2 {
				name
				type
				pk
			}
			type
			name
			importConfirmed
			importId
		}
		location {
			id
			name
			type
		}
		manufacturedBy {
			id
			name
			type
		}
		name
		pk
		servicedBy {
			id
			name
			type
		}
		tags
		workOrders {
			priority
			id
			pk
			name
			status
			workOrderId
			locations {
				id
				locationId
				locationType
			}
		}
		incidents {
			priority
			id
			pk
			name
			status
			incidentId
			locations {
				id
				locationId
				locationType
			}
		}
		requests {
			priority
			id
			pk
			name
			status
			requestId
		}
		classifications {
			id
			name
			pk
			type
		}
		updated
		description
		coordinates {
			x
			y
			z
		}
		conditionGrade
		conditionGradeNote
		productName
		productYear
		serialNumber
		installationDate
		installationCost
		warranty
		costOfRepair
		timeframe
		deviationType
		riskLevel
		costAllocation
		consequenceRating
		labels {
			pk
			name
			type
			color
		}
		matterportUrl
		locationScreenshotUrl
	}
`;

export const AssetsClassificationAttachedFileQuery = gql`
	query AssetsClassificationAttachedFileQuery(
		$pkIn: String
		$first: Int
		$last: Int
		$after: String
		$before: String
		$classificationsAsset: [String]
		$type: [String]
		$search: String
	) {
		assets(pkIn: $pkIn) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					classificationAttachedFileEntities(
						first: $first
						last: $last
						after: $after
						before: $before
						search: $search
						type: $type
						classifications: $classificationsAsset
					) {
						pageInfo {
							...PageInfoFragment
						}
						totalCount
						edges {
							node {
								pk
								file {
									contentType
									name
									url
								}
								created
								createdBy {
									name
									lastName
									smallAvatarUrl
									firstName
								}
								classificationsV2 {
									name
									type
									pk
								}
								type
								name
								locations {
									pk
								}
							}
						}
					}
				}
			}
			totalCount
		}
	}
	${PageInfoFragment}
`;

export const AssetsExportFragment = gql`
	fragment AssetsExportFragment on AssetObjectTypeConnection {
		exportUrl
	}
`;

export const AssetsQuery = gql`
	query AssetsQuery(
		$first: Int
		$last: Int
		$after: String
		$before: String
		$search: String
		$types: String
		$classificationsAsset: [String]
		$locationsAsset: [UUID]
		$includeFromIfc: Boolean
		$isExportQuery: Boolean = false
		$exportColumns: [String]
		$costAllocation: [String]
		$deviationType: [String]
		$riskLevel: [String]
		$timeframe: [String]
		$consequenceRating: [String]
		$costOfRepair: Decimal
		$costOfRepair_Gt: Decimal
		$costOfRepair_Gte: Decimal
		$costOfRepair_Isnull: Boolean
		$costOfRepair_Lt: Decimal
		$costOfRepair_Lte: Decimal
		$sortBy: String
		$name_Iexact: String
		$conditionGrade: Int
		$conditionGrade_In: [String]
		$labels: [UUID]
	) {
		assets(
			first: $first
			after: $after
			before: $before
			last: $last
			search: $search
			typesIn: $types
			classifications: $classificationsAsset
			locations: $locationsAsset
			includeFromIfc: $includeFromIfc
			exportColumns: $exportColumns
			costAllocation: $costAllocation
			deviationType: $deviationType
			riskLevel: $riskLevel
			consequenceRating: $consequenceRating
			timeframe: $timeframe
			costOfRepair: $costOfRepair
			costOfRepair_Gt: $costOfRepair_Gt
			costOfRepair_Gte: $costOfRepair_Gte
			costOfRepair_Isnull: $costOfRepair_Isnull
			costOfRepair_Lt: $costOfRepair_Lt
			costOfRepair_Lte: $costOfRepair_Lte
			sortBy: $sortBy
			name_Iexact: $name_Iexact
			conditionGrade: $conditionGrade
			conditionGrade_In: $conditionGrade_In
			labels: $labels
		) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					...AssetFragment
				}
			}
			totalCount
			...AssetsExportFragment @include(if: $isExportQuery)
		}
	}
	${AssetFragment}
	${PageInfoFragment}
	${AssetsExportFragment}
`;

export const SearchAssetsQuery = gql`
	query SearchAssetsQuery(
		$search: String
		$first: Int
		$last: Int
		$after: String
		$before: String
		$pkIn: String
		$globalId: String
	) {
		assets(
			search: $search
			first: $first
			last: $last
			after: $after
			before: $before
			pkIn: $pkIn
			ifcIdGlobalId: $globalId
		) {
			pageInfo {
				endCursor
			}
			edges {
				cursor
				node {
					...AssetFragment
				}
			}
			totalCount
		}
	}
	${AssetFragment}
`;

export const LoadAssetsTypes = gql`
	query LoadAssetsTypes(
		$after: String
		$before: String
		$first: Int
		$id: ID
		$ifcType: String
		$ifcType_Isnull: Boolean
		$last: Int
		$name: String
		$name_Icontains: String
		$pk: String
	) {
		assetTypes(
			after: $after
			before: $before
			first: $first
			id: $id
			ifcType: $ifcType
			ifcType_Isnull: $ifcType_Isnull
			last: $last
			name: $name
			name_Icontains: $name_Icontains
			pk: $pk
		) {
			pageInfo {
				endCursor
			}
			edges {
				cursor
				node {
					name
					ifcType
					id
					pk
				}
			}
		}
	}
`;

export const AssetsForDtQuery = gql`
	query AssetsForDtQuery($locationId: UUID) {
		assets(
			first: 100
			locationId: $locationId
			hasCoordinates: true
			includeFromIfc: false
		) {
			edges {
				node {
					pk
					name
					description
					coordinates {
						x
						y
						z
					}
					type {
						name
					}
					assetAttachedFiles {
						file {
							contentType
							url
						}
					}
					classifications {
						id
						name
						pk
						type
					}
				}
			}
		}
	}
`;
